import { isValidElement, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  QuickStudyRunResult,
  WorkflowQuickStudyFinding,
  WorkflowRun,
} from '@digital-twins/shared-types'

import { ArtifactEditorShell } from '@/components/artifact-editor/ArtifactEditorShell'
import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { buildDemographicCutGroups } from '@/components/quick-study-results/divergingStacked'
import { FindingServerSpecPlot } from '@/components/quick-study-results/featuredPlots/FindingServerSpecPlot'
import {
  analystSpecsFromPayload,
  hasFindingAnalystSpec,
} from '@/components/quick-study-results/featuredPlots/findingAnalystSpecs'
import {
  QuickStudyCutPlotsContext,
  quickStudyCutPlotsSourceFor,
} from '@/components/quick-study-results/quickStudyCutPlots'
import { QuickStudyNounProvider } from '@/components/quick-study-results/quickStudyNoun'
import { InsightCard } from '@/components/quick-study-results/InsightCard/InsightCard'
import { Result } from '@/components/quick-study-results/quickStudyTypes'
import { StudyDocBody, StudyDocPrintButton } from '@/components/study-doc'
import { ContentLanguageProvider } from '@/i18n/contentLanguageContext'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { composeThreadStudyDoc, populationIdFromRunInputs } from './composeThreadStudyDoc'
import { ArtifactPreviewCard } from './ThreadRunPane/ArtifactPreviewCard'
import { artifactDisplayLabel, localizeArtifactCategory } from './ThreadRunPane/artifactDisplayLabel'
import { studyDocArtifactFromRun, ThreadArtifactItem } from './ThreadRunPane/collectThreadArtifacts'
import { useThreadArtifactTips } from './ThreadRunPane/useThreadArtifactTips'

/**
 * What the twins actually said, IN THE THREAD.
 *
 * After Step 10 there is no Quick Study page to link to, so a run that spent
 * money has to report back where the reader already is: at the end of the
 * conversation, under the gate they approved. That is D2, and it is why this
 * renders in the reading column rather than in the run pane, which is for
 * watching progress.
 *
 * The write-up is THE POET StudyDoc inside `ArtifactPreviewCard` (Edit ·
 * Copy · Expand). InsightCard renders plots only, never the thread
 * narrative. A finding without `study_doc` degrades to digest or plot-only
 * — we do not fall back to card prose.
 *
 * WHY THE ROWS TRAVEL. `InsightCard` and `PlotRenderer` aggregate client-side
 * from `(question, responses)` — the distribution, the segment cuts, the theme
 * shares and every quote are computed in the browser from the response rows.
 * That is why D8 keeps the rows inline on the finding instead of capping them
 * or replacing them with a reference: a finding with no rows and no server spec
 * renders a headline and an empty chart. When a finding carries an analyst spec,
 * PlotRenderer prefers Step 7's adapter; rows stay for cut-switching and
 * dev-mode parity.
 *
 * DEGRADES, NEVER THROWS. A finding produced before the whole result was
 * carried has only the five summary fields, and a live run can hand over a
 * partial result — no narrative, or a verdict the grounding gate withheld.
 * Neither is an error state: the first falls back to the digest below, and the
 * second is the card's own normal rendering, since withholding an ungrounded
 * verdict is the grounding contract working.
 */

interface ThreadRunResultsProps {
  run: WorkflowRun | null
  className?: string
  /**
   * When the reader picks a cut, the attribute key (`segment_by`) is reported
   * so a parent can put it on the existing run-input bag.
   */
  onSegmentByChange?: (segmentBy: string | null) => void
  /**
   * Opens the full-viewport StudyDoc / artifact surface (ChatGPT Expand).
   */
  onExpandArtifact?: (item: ThreadArtifactItem) => void
}

/** One completed twin test on the run, with the findings it produced. */
interface ResultBlock {
  stepId: string
  stepIndex: number
  title: string
  panelSize: number
  /** Twins actually drawn; smaller than `panelSize` on a short pool. Absent on legacy outputs. */
  drawnPanelSize?: number
  /** Nest's short-pool note, present when the drawn panel is smaller than requested. */
  shortPanelNote?: string
  findings: Array<WorkflowQuickStudyFinding>
}

/**
 * The result of a `quick_study` step, as a `Result` the card can render.
 *
 * The payload is python's own result object, carried verbatim by
 * `toTwinTestFinding` (C2), so it already IS this shape — the reads below only
 * fill the two fields the card needs and a partial frame may not carry. Nothing
 * is enumerated: the object is spread first, so a field python adds tomorrow
 * reaches the renderer without passing through here.
 */
export function toRenderableResult(finding: WorkflowQuickStudyFinding): Result | null {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }

  const carried = payload as Record<string, unknown>
  const responses = Array.isArray(carried.responses) ? carried.responses : []
  const analystSpecs = analystSpecsFromPayload(carried)
  // A payload with no rows and no server spec cannot draw anything: the
  // charts aggregate from the rows, so the digest below is the honest
  // rendering, not an empty chart with a confident headline over it.
  // A spec with no rows still renders — the server is the numeric authority.
  if (responses.length === 0 && !hasFindingAnalystSpec(analystSpecs)) {
    return null
  }

  const plot =
    typeof carried.plot === 'string' && carried.plot.trim()
      ? carried.plot
      : hasFindingAnalystSpec(analystSpecs)
        ? 'bar'
        : carried.plot

  return {
    ...carried,
    // The gate's own wording for this question. The result carries a title too
    // in the ordinary case; this only supplies one when it does not, so the
    // exhibit is never labelled with an empty string.
    title: typeof carried.title === 'string' && carried.title.trim()
      ? carried.title
      : finding.question,
    headline:
      typeof carried.headline === 'string' && carried.headline.trim()
        ? carried.headline
        : finding.headline,
    plot,
    responses,
    analystCharts: analystSpecs.charts.length > 0 ? analystSpecs.charts : undefined,
    analystTables: analystSpecs.tables.length > 0 ? analystSpecs.tables : undefined,
  } as unknown as Result
}

/**
 * The five summary fields, for a finding that carries nothing else.
 *
 * Only reachable for a finding persisted before the whole result travelled, or
 * one whose result came back with no response rows. It says what is known and
 * stops: no chart, because there is nothing to aggregate, and no invented
 * confidence over an absence.
 */
function FindingDigest(props: {
  finding: WorkflowQuickStudyFinding
  position: number
}) {
  const { finding, position } = props

  const share =
    finding.topAnswerLabel && finding.topAnswerPercent > 0
      ? `${finding.topAnswerLabel} · ${Math.round(finding.topAnswerPercent)}%`
      : finding.topAnswerLabel

  return (
    <div
      className="rounded-xl border border-border bg-card p-3"
      data-testid={`thread-run-finding-digest-${position}`}
    >
      <Text size="xs" color="muted" className="leading-5">
        {finding.question}
      </Text>
      {finding.headline ? (
        <Heading level={3} size="sm" weight="medium" className="mt-1.5 leading-6">
          {finding.headline}
        </Heading>
      ) : null}
      {share ? (
        <Text
          size="sm"
          className="mt-1.5 leading-6 text-foreground"
          data-testid={`thread-run-finding-top-${position}`}
        >
          {share}
        </Text>
      ) : null}
      {finding.verbatim ? (
        <Text
          size="sm"
          color="muted"
          className="mt-2 leading-6"
          data-testid={`thread-run-finding-verbatim-${position}`}
        >
          &ldquo;{finding.verbatim}&rdquo;
        </Text>
      ) : null}
    </div>
  )
}

/** Every completed twin test on the run, in step order. */
export function collectResultBlocks(run: WorkflowRun): Array<ResultBlock> {
  const blocks: Array<ResultBlock> = []
  for (const step of run.steps) {
    // `outputOverride ?? output` for the same reason the gate reads it that
    // way: what was approved wins over the first draft.
    const output = step.outputOverride ?? step.output
    if (!output || output.kind !== 'quick_study') {
      continue
    }
    if (output.findings.length === 0) {
      continue
    }
    blocks.push({
      stepId: step.stepId,
      stepIndex: step.stepIndex,
      title: step.title,
      panelSize: output.panelSize,
      ...(output.drawnPanelSize !== undefined
        ? { drawnPanelSize: output.drawnPanelSize }
        : {}),
      ...(output.shortPanelNote ? { shortPanelNote: output.shortPanelNote } : {}),
      findings: output.findings,
    })
  }
  return blocks
}

function responsesFromFindings(
  findings: Array<WorkflowQuickStudyFinding>,
): Parameters<typeof buildDemographicCutGroups>[0] {
  const rows: Array<unknown> = []
  for (const finding of findings) {
    const payload = finding.result
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      continue
    }
    const responses = (payload as Record<string, unknown>).responses
    if (!Array.isArray(responses)) {
      continue
    }
    rows.push(...responses)
  }
  return rows as Parameters<typeof buildDemographicCutGroups>[0]
}

export function cutGroupsFromRun(
  run: WorkflowRun | null | undefined,
): Array<CompareSegmentsGroup> {
  if (!run) {
    return []
  }

  const blocks = collectResultBlocks(run)
  return buildDemographicCutGroups(
    responsesFromFindings(blocks.flatMap((block) => block.findings)),
  )
}

/**
 * Spec-only findings have no rows for InsightCard.hasData. StudyDocBody
 * already accepts a ReactNode plot lookup — render Step 7's adapter directly.
 */
export function plotLookupForResult(result: Result): Result | ReactNode {
  const charts = result.analystCharts ?? []
  const tables = result.analystTables ?? []
  if ((result.responses?.length ?? 0) > 0 || (charts.length === 0 && tables.length === 0)) {
    return result
  }

  return <FindingServerSpecPlot charts={charts} tables={tables} />
}

/**
 * The typed read of one question (`QuickStudyRunResult.read`), or null.
 *
 * A new quick study has no StudyDoc: python returns `read` (headline plus an
 * optional takeaway) on every question and the screen draws the plot from the
 * rows next to it. A finding that carries a `study_doc` is a legacy run and
 * keeps rendering the doc, whatever else it carries.
 */
export function typedReadFromFinding(
  finding: WorkflowQuickStudyFinding,
): Pick<QuickStudyRunResult['read'], 'headline' | 'takeaway'> | null {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }
  const carried = payload as Record<string, unknown>
  if (typeof carried.study_doc === 'string' && carried.study_doc.trim()) {
    return null
  }
  const read = carried.read
  if (!read || typeof read !== 'object' || Array.isArray(read)) {
    return null
  }
  const { headline, takeaway } = read as Record<string, unknown>
  if (typeof headline !== 'string' || !headline.trim()) {
    return null
  }
  return {
    headline: headline.trim(),
    takeaway: typeof takeaway === 'string' ? takeaway.trim() : '',
  }
}

/**
 * True when the run is a new typed result: no finding carries a StudyDoc and
 * at least one carries the typed shape (a `read` object or an `n_asked`
 * count), whether or not its headline came back empty.
 */
export function isTypedResultRun(blocks: Array<ResultBlock>): boolean {
  const findings = blocks.flatMap((block) => block.findings)
  return (
    findings.length > 0 &&
    findings.every((finding) => !studyDocTextOf(finding)) &&
    findings.some(carriesTypedResultShape)
  )
}

function carriesTypedResultShape(finding: WorkflowQuickStudyFinding): boolean {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false
  }
  const read = (payload as Record<string, unknown>).read
  const hasRead = Boolean(read) && typeof read === 'object' && !Array.isArray(read)
  return hasRead || askedCountOf(finding) !== null
}

function studyDocTextOf(finding: WorkflowQuickStudyFinding): string {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return ''
  }
  const raw = (payload as Record<string, unknown>).study_doc
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * How many respondents a question was asked (python's `n_asked`, D5), or null
 * when the result does not say. A follow-up with `visibleIf` is asked only of
 * the respondents it applies to (D6): 0 means it applied to nobody.
 */
export function askedCountOf(finding: WorkflowQuickStudyFinding): number | null {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }
  const raw = (payload as Record<string, unknown>).n_asked
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : null
}

interface NotAskedQuestionProps {
  finding: WorkflowQuickStudyFinding
  index: number
}

function NotAskedQuestion(props: NotAskedQuestionProps) {
  const { finding, index } = props

  const { t } = useTranslation('app')

  return (
    <div
      className="rounded-xl border border-border bg-card p-3"
      data-testid={`thread-run-result-not-asked-${index}`}
    >
      <Text size="xs" color="muted" className="leading-5">
        {finding.question}
      </Text>
      <Text size="sm" className="mt-1.5 leading-6 text-foreground">
        {t('threads.detail.runPane.notAsked')}
      </Text>
    </div>
  )
}

interface TypedQuestionResultProps {
  finding: WorkflowQuickStudyFinding
  index: number
  panelSize: number
  populationId?: string
}

function TypedQuestionResult(props: TypedQuestionResultProps) {
  const { finding, index, panelSize, populationId } = props

  const { t } = useTranslation('app')

  const result = toRenderableResult(finding)
  const read = typedReadFromFinding(finding)
  const askedCount = askedCountOf(finding)
  // A follow-up asked to part of the panel by its OWN condition: its base is
  // the respondents asked. Never inferred from the counts alone: a short pool
  // makes every question's base smaller than the requested size.
  const isPartialBase =
    finding.conditional === true &&
    askedCount !== null &&
    askedCount > 0 &&
    panelSize > 0 &&
    askedCount < panelSize

  const plot = result ? plotLookupForResult(result) : null
  const plotNode = result ? (
    isValidElement(plot) ? (
      plot
    ) : (
      <InsightCard
        result={result}
        chrome="plot-card"
        region="plot"
        populationId={populationId}
      />
    )
  ) : null

  const askedBase = isPartialBase ? (
    <Text
      size="xs"
      color="muted"
      className="leading-5"
      data-testid={`thread-run-result-asked-base-${index}`}
    >
      {t('threads.detail.runPane.askedBase', { asked: askedCount, total: panelSize })}
    </Text>
  ) : null

  if (askedCount === 0) {
    return <NotAskedQuestion finding={finding} index={index} />
  }

  if (!result) {
    return <FindingDigest finding={finding} position={index} />
  }

  return (
    <div className="space-y-3" data-testid={`thread-run-result-read-${index}`}>
      {read ? (
        <Heading
          level={3}
          size="sm"
          weight="medium"
          className="leading-6"
          data-testid={`thread-run-result-headline-${index}`}
        >
          {read.headline}
        </Heading>
      ) : null}
      {askedBase}
      {plotNode}
      {read && read.takeaway ? (
        <Text
          size="sm"
          className="leading-6 text-foreground"
          data-testid={`thread-run-result-takeaway-${index}`}
        >
          {read.takeaway}
        </Text>
      ) : null}
    </div>
  )
}

function contentLanguageFromRun(run: WorkflowRun): string {
  const raw = run.inputs?.language
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return 'en'
}

interface ShortPanelNoteProps {
  blocks: Array<ResultBlock>
}

/**
 * One note for the run when a twin test drew fewer respondents than requested.
 * Localized from the counts when the output carries the drawn size; a legacy
 * output shows Nest's own note as written.
 */
function ShortPanelNote(props: ShortPanelNoteProps) {
  const { blocks } = props

  const { t } = useTranslation('app')

  const shortBlock = blocks.find(
    (block) =>
      Boolean(block.shortPanelNote) ||
      (block.drawnPanelSize !== undefined && block.drawnPanelSize < block.panelSize),
  )

  if (!shortBlock) {
    return null
  }

  const hasCounts =
    shortBlock.drawnPanelSize !== undefined &&
    shortBlock.drawnPanelSize > 0 &&
    shortBlock.drawnPanelSize < shortBlock.panelSize
  const copy = hasCounts
    ? t('threads.detail.runPane.shortPanel', {
        drawn: shortBlock.drawnPanelSize,
        requested: shortBlock.panelSize,
      })
    : (shortBlock.shortPanelNote ?? '')

  if (!copy) {
    return null
  }

  return (
    <Text
      size="xs"
      color="muted"
      className="leading-5"
      data-testid="thread-run-result-short-panel-note"
    >
      {copy}
    </Text>
  )
}

export function ThreadRunResults(props: ThreadRunResultsProps) {
  const { run, className, onExpandArtifact } = props

  const { t } = useTranslation('app')

  const [isEditingStudyDoc, setIsEditingStudyDoc] = useState(false)

  const blocks = useMemo(() => (run ? collectResultBlocks(run) : []), [run])
  const studyDocItem = useMemo(() => studyDocArtifactFromRun(run), [run])
  const { tipsByIdentity, canEdit, upsertLocalTip } = useThreadArtifactTips(
    run?.token ?? null,
  )
  // W6-QS-c: the run every card's Compare-segments pick asks cut plots for.
  const cutPlotsSource = useMemo(() => quickStudyCutPlotsSourceFor(run), [run])

  if (!run) {
    return null
  }

  if (blocks.length === 0) {
    return null
  }

  const populationId = populationIdFromRunInputs(run.inputs)

  if (isTypedResultRun(blocks)) {
    const typedFindings = blocks.flatMap((block) =>
      block.findings.map((finding) => ({
        finding,
        panelSize: block.drawnPanelSize ?? block.panelSize,
      })),
    )
    return (
      <ContentLanguageProvider language={contentLanguageFromRun(run)}>
        <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
          <QuickStudyNounProvider
            noun="respondents"
            segmentLabel={run.populationFilter ? (run.segmentLabel ?? null) : null}
          >
            <div className={cn('space-y-6', className)} data-testid="thread-run-results">
              <ShortPanelNote blocks={blocks} />
              {typedFindings.map(({ finding, panelSize }, index) => (
                <TypedQuestionResult
                  key={`typed-${index}`}
                  finding={finding}
                  index={index}
                  panelSize={panelSize}
                  populationId={populationId}
                />
              ))}
            </div>
          </QuickStudyNounProvider>
        </QuickStudyCutPlotsContext.Provider>
      </ContentLanguageProvider>
    )
  }

  const studyDoc = composeThreadStudyDoc(
    blocks,
    toRenderableResult,
    null,
    populationId,
  )
  const studyDocPlots = Object.fromEntries(
    Object.entries(studyDoc.plots).map(([plotId, result]) => [plotId, plotLookupForResult(result)]),
  )
  const hasStudyDocMarkdown = studyDoc.markdown.trim().length > 0

  const digestFindings = blocks.flatMap((block) =>
    block.findings
      .map((finding, position) => ({
        finding,
        position,
        stepIndex: block.stepIndex,
      }))
      .filter(({ finding }) => !toRenderableResult(finding)),
  )

  const studyDocBody = (
    <>
      {hasStudyDocMarkdown ? (
        <div data-testid="thread-run-findings">
          <StudyDocBody
            markdown={studyDoc.markdown}
            plots={studyDocPlots}
            quotes={studyDoc.quotes}
            populationId={studyDoc.populationId}
            decisionConfidence={studyDoc.decisionConfidence}
            strips={studyDoc.strips}
            plotRegion="plot"
            layout="lite-magazine"
          />
        </div>
      ) : null}

      {digestFindings.map(({ finding, position, stepIndex }) => (
        <FindingDigest
          key={`digest-${stepIndex}-${position}`}
          finding={finding}
          position={position}
        />
      ))}
    </>
  )

  const tip = studyDocItem
    ? (tipsByIdentity.get(studyDocItem.key) ?? null)
    : null
  const displayBody = studyDocItem
    ? reconcileArtifactBody(studyDocItem.body, tip)
    : ''
  const studyDocLabel = studyDocItem
    ? artifactDisplayLabel({ ...studyDocItem, body: displayBody })
    : null
  const studyDocCategory = studyDocLabel
    ? localizeArtifactCategory(studyDocLabel.category, studyDocLabel.letter)
    : null
  const studyDocAccessibleName = studyDocLabel
    ? studyDocCategory
      ? `${studyDocCategory}: ${studyDocLabel.headline}`
      : studyDocLabel.headline
    : t('threads.detail.runPane.studyReport')
  const allowStudyDocEdit = Boolean(canEdit && studyDocItem)

  const framedStudyDoc = studyDocItem ? (
    isEditingStudyDoc && allowStudyDocEdit && studyDocItem.runToken ? (
      <div data-testid="thread-run-study-doc">
        <ArtifactEditorShell
          value={displayBody}
          accessibleName={studyDocAccessibleName}
          canEdit={allowStudyDocEdit}
          isEditing
          peek={false}
          original={tip?.original ?? true}
          onExpand={
            onExpandArtifact ? () => onExpandArtifact(studyDocItem) : undefined
          }
          onDone={() => setIsEditingStudyDoc(false)}
          onCancel={() => setIsEditingStudyDoc(false)}
          autosave={{
            runToken: studyDocItem.runToken,
            stepId: studyDocItem.stepId,
            artifactIdentity: studyDocItem.key,
            machineBody: studyDocItem.body,
            stepIndex: studyDocItem.stepIndex,
            existingTip: tip,
            onSaved: upsertLocalTip,
          }}
          className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
          testIdPrefix="artifact-preview-card"
        />
      </div>
    ) : (
      <div data-testid="thread-run-study-doc">
        <ArtifactPreviewCard
          item={studyDocItem}
          tip={tip}
          original={tip?.original ?? true}
          canEdit={allowStudyDocEdit}
          clamp={false}
          peek
          showHeader={false}
          onExpand={onExpandArtifact}
          headerExtra={
            <StudyDocPrintButton accessibleName={studyDocAccessibleName} />
          }
          onEdit={
            allowStudyDocEdit
              ? () => {
                  setIsEditingStudyDoc(true)
                }
              : undefined
          }
          className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
        >
          {studyDocBody}
        </ArtifactPreviewCard>
      </div>
    )
  ) : (
    <div
      className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
      data-testid="thread-run-study-doc"
    >
      {studyDocBody}
    </div>
  )

  return (
    <ContentLanguageProvider language={contentLanguageFromRun(run)}>
      <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
        {/* Respondents, and the run's segment on every base footnote of a
            filtered run ("Base: 40 respondents, women 21 to 34"). */}
        <QuickStudyNounProvider
          noun="respondents"
          segmentLabel={run.populationFilter ? (run.segmentLabel ?? null) : null}
        >
          <div className={cn('space-y-4', className)} data-testid="thread-run-results">
            <ShortPanelNote blocks={blocks} />
            {framedStudyDoc}
          </div>
        </QuickStudyNounProvider>
      </QuickStudyCutPlotsContext.Provider>
    </ContentLanguageProvider>
  )
}
